import { useMemo, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { useGetContentLibrary } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentGeneration/hooks/useContentGenerate';
import { GetEntityMetadataResponseRecord } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentGeneration/Generate/types';
import { getProductImageUrl } from 'src/utils/image';
import { EnhancedLibraryRecord, LibraryResponse, LibraryVersion } from '../types/libraryTypes';
import { useBeaconFilters } from 'src/utils/Hooks/useDorisApplyEntityFilters';
import { useFilterChangeDetection } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentGeneration/Generate/components/ProductTable/useProductTable';

interface UseLibraryParams {
  retailerId: number;
  searchTerm?: string;
  pageNumber: number;
  pageSize: number;
  setPageNumber?: (page: number) => void;
}

export const useLibrary = ({
  retailerId,
  searchTerm,
  pageNumber,
  pageSize,
  setPageNumber
}: UseLibraryParams) => {
  const { appFilters } = useBeaconFilters();
  
  const filtersChanged = useFilterChangeDetection(appFilters);

  useEffect(() => {
    if (filtersChanged && setPageNumber) {
      setPageNumber(1);
    }
  }, [filtersChanged, setPageNumber]);

  const libraryRequest = useMemo(() => ({
    retailerId,
    searchTerm,
    pageNumber,
    filterNullLibraryStatus: true,
    pageSize: pageSize + 1,  // Fetch one extra to check for next page
    // Use the same filter key names as in getInitialProductData
    ...(appFilters?.categoryFilters &&
      appFilters.categoryFilters.length > 0 && { categoryIds: appFilters.categoryFilters }),
    ...(appFilters?.subcategoryFilters &&
      appFilters.subcategoryFilters.length > 0 && { subcategoryIds: appFilters.subcategoryFilters }),
    ...(appFilters?.segmentFilters && { segmentIds: [appFilters.segmentFilters.id] }),
    ...(appFilters?.businessUnitFilters && { businessUnitFilters: appFilters.businessUnitFilters }),
    ...(appFilters?.retailPriceFilter && { retailPriceFilter: appFilters.retailPriceFilter }),
    // Derive selected brandIds from URL filter param (b)
    ...(() => {
      try {
        const params = new URLSearchParams(window.location.search);
        const raw = params.get('filter');
        if (!raw) return {};
        const parsed = JSON.parse(raw);
        const ids = Array.isArray(parsed?.b) ? parsed.b.map((x: { i: number | string }) => Number(x.i)).filter(Boolean) : [];
        return ids.length > 0 ? { brandIds: ids } : {};
      } catch {
        return {};
      }
    })()
  }), [retailerId, searchTerm, pageNumber, pageSize, appFilters]);

  const { data, isLoading, error, refetch } = useGetContentLibrary(libraryRequest);

  const stacklineSkus = useMemo(() => {
    if (!data?.records) return [];
    return [...new Set(data.records.map(record => record.stacklineSku).filter(Boolean))];
  }, [data?.records]);

  const { data: productMetadata } = useQuery({
    queryKey: ['productMetadata', stacklineSkus, retailerId],
    queryFn: async () => {
      if (stacklineSkus.length === 0) return [];
      
      const { data: response } = await axios.post<GetEntityMetadataResponseRecord[]>(
        '/api/beacon/GetEntityMetadataInBulk',
        {
          entityType: 'product',
          entityIds: stacklineSkus,
          retailerId
        }
      );
      return response;
    },
    enabled: stacklineSkus.length > 0,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const userIds = useMemo(() => {
    if (!data?.records) return [];
    const allUserIds = new Set<string>();
    data.records.forEach(record => {
      record.versions.forEach(version => {
        if (version.createdBy) {
          allUserIds.add(version.createdBy);
        }
      });
    });
    return Array.from(allUserIds);
  }, [data?.records]);

  // Extract profile IDs from data for profile metadata query
  const profileIds = useMemo(() => {
    if (!data?.records) return [];
    const allProfileIds = new Set<string>();
    data.records.forEach(record => {
      record.versions.forEach(version => {
        if (version.profileId) {
          allProfileIds.add(version.profileId);
        }
      });
    });
    return Array.from(allProfileIds);
  }, [data?.records]);

  const { data: userMetadata, isLoading: isLoadingUserMetadata } = useQuery({
    queryKey: ['userMetadata', userIds],
    queryFn: async () => {
      if (userIds.length === 0) return {};
      const response = await axios.post('/api/user/GetUserProfilesFromUserIds', userIds);
      return response.data as Record<string, { firstName: string; lastName: string; email: string }>;
    },
    enabled: userIds.length > 0,
    staleTime: 5 * 60 * 1000,
  });

  // Fetch profile metadata for profile names
  const { data: profileMetadata } = useQuery({
    queryKey: ['profileMetadata', profileIds],
    queryFn: async () => {
      if (profileIds.length === 0) return {};
      
      // Fetch all profiles and create a map of profileId -> profileName
      const profileMap: Record<string, string> = {};
      
      // Use Promise.all to fetch all profiles in parallel
      await Promise.all(
        profileIds.map(async (profileId) => {
          try {
            const response = await axios.get('/api/beaconcontent/GetProfile', {
              params: { profileId }
            });
            if (response.data.isSuccess && response.data.data.profileName) {
              profileMap[profileId] = response.data.data.profileName;
            }
          } catch (error) {
            console.warn(`Failed to fetch profile ${profileId}:`, error);
          }
        })
      );
      
      return profileMap;
    },
    enabled: profileIds.length > 0,
    staleTime: 5 * 60 * 1000,
  });

  const enhancedData = useMemo((): { records: EnhancedLibraryRecord[]; totalCount: number; hasNextPage: boolean } => {
    const libraryResponse = data as LibraryResponse;
    
    // Determine if there's a next page by checking if we got more records than requested
    const hasNextPage = (libraryResponse?.records?.length || 0) > pageSize;
    
    const recordsToShow = libraryResponse?.records?.slice(0, pageSize) || [];
    const stacklineSkuToMetadataMap = productMetadata?.reduce((acc, record) => {
      acc.set(record.stacklineSku, record);
      return acc;
    }, new Map<string, GetEntityMetadataResponseRecord>()) || new Map();

    const enhancedRecords: EnhancedLibraryRecord[] = recordsToShow.map(record => {
      const productMetadataRecord = stacklineSkuToMetadataMap.get(record.stacklineSku);
      
      const mostRecentVersion = record.versions.length > 0 
        ? record.versions.reduce((latest, current) => 
            new Date(current.updatedAt) > new Date(latest.updatedAt) ? current : latest
          )
        : null;

      const getUserDisplayName = (userId: string) => {
        if (userId === 'content-generation-workflow') {
          return 'Stackline';
        }
        const userData = userMetadata?.[userId];
        if (userData?.firstName && userData?.lastName) {
          return `${userData.firstName} ${userData.lastName}`;
        }
        return userId;
      };

      const name = getUserDisplayName(mostRecentVersion?.createdBy || 'content-generation-workflow');

      return {
        ...record,
        product: productMetadataRecord,
        productImage: getProductImageUrl(record.stacklineSku),
        productName: record.title,
        lastUpdatedBy: {
          name: name,           
          userId: mostRecentVersion?.createdBy|| 'content-generation-workflow',
          date: mostRecentVersion ? `Generated on ${new Date(mostRecentVersion.updatedAt).toLocaleDateString('en-US', { 
            year: 'numeric', 
            month: 'short', 
            day: 'numeric' 
          })}` : 'Unknown date'
        },
        enhancedVersions: record.versions.map(version => ({
          ...version,
          name: getUserDisplayName(version.createdBy),
          profileName: profileMetadata?.[version.profileId] || version.profileId
        }))
      };
    }) || [];

    return {
      records: enhancedRecords,
      totalCount: recordsToShow.length,  // Only count the records we're showing
      hasNextPage
    };
  }, [data, productMetadata, userMetadata, profileMetadata, pageSize]);

  return {
    data: enhancedData,
    isLoading,
    isLoadingUserMetadata,
    error,
    refetch
  };
};
